import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowRight, Building2, LineChart, ShieldCheck, Users } from "lucide-react";
import ContactForm from "@/components/ContactForm";

const PROJECT_IMAGES = [
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=70",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=70",
  "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=70",
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=70",
];

const HERO_IMAGE = "https://images.unsplash.com/photo-1554435493-93422e8220c8?auto=format&fit=crop&w=1600&q=75";
const URBAN_IMAGE = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1600&q=75";

const VALUE_ICONS = [Building2, ShieldCheck, Users, LineChart];

export default function Home() {
  const { t } = useTranslation();

  const marquee = t("marquee", { returnObjects: true });
  const projects = t("projects.list", { returnObjects: true });
  const steps = t("steps.list", { returnObjects: true });
  const stats = t("stats.items", { returnObjects: true });
  const values = t("values.list", { returnObjects: true });
  const faqs = t("faq.items", { returnObjects: true });

  return (
    <main id="main">
      {/* HERO */}
      <section className="relative pt-[120px] md:pt-[160px] pb-16 md:pb-24 overflow-hidden">
        <div className="zv-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 zv-anim">
            <div className="zv-mono-tag text-[#c8622a] mb-6">{t("hero.eyebrow")}</div>
            <h1 className="zv-serif text-[46px] md:text-[80px] lg:text-[96px] leading-[0.95] tracking-tight text-[#0d1117]">
              <span className="block">{t("hero.title_1")}</span>
              <span className="block italic text-[#c8622a]">{t("hero.title_2")}</span>
              <span className="block">{t("hero.title_3")}</span>
            </h1>
            <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-[#2a303b]">
              {t("hero.lead")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#intro" className="zv-btn zv-btn-primary">
                {t("hero.cta_primary")} <ArrowRight size={16} />
              </a>
              <a href="#contact" className="zv-btn zv-btn-ghost">
                {t("hero.cta_secondary")}
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              {[t("hero.badge_1"), t("hero.badge_2"), t("hero.badge_3")].map((b) => (
                <span key={b} className="inline-flex items-center gap-2 border border-[#0d1117]/20 rounded-full px-4 py-2 text-[12px] tracking-wide">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c8622a]"></span>
                  {b}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[3/4] rounded-[28px] overflow-hidden zv-grain">
              <img
                src={HERO_IMAGE}
                alt="Torres residenciales en Argentina"
                loading="eager"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117]/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 text-[#f6f0e2]">
                <div className="zv-mono-tag text-[#f6f0e2]/70">Buenos Aires · 2026</div>
                <div className="zv-serif text-2xl mt-1">Palermo · Retiro · Nuñez</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block">
              <div className="bg-[#0d1117] text-[#f6f0e2] rounded-2xl p-4 shadow-2xl">
                <div className="zv-mono-tag text-[#f6f0e2]/60">{t("hero.scroll")}</div>
                <ArrowDown size={22} className="mt-2" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section aria-hidden="true" className="border-y border-[#0d1117]/10 py-6 overflow-hidden bg-[#f0e7d3]">
        <div className="zv-marquee-track whitespace-nowrap zv-serif text-[36px] md:text-[52px] text-[#0d1117]/80">
          {[...marquee, ...marquee].map((c, i) => (
            <span key={i} className="flex items-center gap-12 shrink-0">
              <span>{c}</span>
              <span className="w-3 h-3 rounded-full bg-[#c8622a]"></span>
            </span>
          ))}
        </div>
      </section>

      {/* Intro */}
      <section id="intro" className="py-24 md:py-32">
        <div className="zv-container grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <div className="zv-mono-tag text-[#c8622a] mb-4">{t("intro.eyebrow")}</div>
            <h2 className="zv-serif text-4xl md:text-5xl leading-tight">{t("intro.title")}</h2>
            <div className="mt-8 aspect-[4/5] rounded-[28px] overflow-hidden">
              <img
                src={URBAN_IMAGE}
                alt="Nueva construcción residencial"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 zv-prose">
            <p>{t("intro.p1")}</p>
            <p>{t("intro.p2")}</p>
            <p>{t("intro.p3")}</p>
            <p>{t("intro.p4")}</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 md:py-32 bg-[#0d1117] text-[#f6f0e2] rounded-t-[40px]">
        <div className="zv-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="zv-mono-tag text-[#c8622a] mb-4">{t("how.eyebrow")}</div>
              <h2 className="zv-serif text-5xl md:text-7xl leading-[1]">{t("how.title")}</h2>
            </div>
            <div className="lg:col-span-5 text-white/70 text-[16px] leading-relaxed">
              <p>{t("how.p1")}</p>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s) => (
              <div key={s.n} className="rounded-2xl p-6 border border-white/10 hover:border-[#c8622a] transition-colors bg-white/[0.02]">
                <div className="zv-mono-tag text-[#c8622a] mb-6">{s.n}</div>
                <div className="zv-serif text-2xl mb-2">{s.t}</div>
                <div className="text-[14px] text-white/70 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="space-y-6 text-white/75 text-[16px] leading-relaxed">
              <p>{t("how.p2")}</p>
              <p>{t("how.p3")}</p>
            </div>
            <div className="space-y-6 text-white/75 text-[16px] leading-relaxed">
              <p>
                {t("how.p4_prefix")}
                <strong className="text-[#c8622a] font-medium">{t("how.p4_bold")}</strong>
                {t("how.p4_suffix")}
              </p>
              <p>{t("how.p5")}</p>
              <p>{t("how.p6")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="projects" className="py-24 md:py-32">
        <div className="zv-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="zv-mono-tag text-[#c8622a] mb-4">{t("projects.eyebrow")}</div>
              <h2 className="zv-serif text-4xl md:text-6xl leading-tight">{t("projects.title")}</h2>
            </div>
            <p className="max-w-md text-[15px] text-[#2a303b]">{t("projects.lead")}</p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((p, i) => (
              <article key={p.name} className="group">
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
                  <img
                    src={PROJECT_IMAGES[i % PROJECT_IMAGES.length]}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-[#f6f0e2] text-[#0d1117] px-3 py-1 rounded-full text-[11px] tracking-wide uppercase">
                    {p.tag}
                  </div>
                </div>
                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <div className="zv-mono-tag text-[#0d1117]/50 mb-2">{p.city}</div>
                    <h3 className="zv-serif text-3xl">{p.name}</h3>
                    <p className="mt-3 text-[15px] text-[#2a303b] leading-relaxed max-w-md">{p.desc}</p>
                  </div>
                  <a href="#contact" aria-label={`${t("nav.contact")} — ${p.name}`} className="shrink-0 w-12 h-12 rounded-full border border-[#0d1117] flex items-center justify-center hover:bg-[#0d1117] hover:text-[#f6f0e2] transition">
                    <ArrowRight size={18} />
                  </a>
                </div>
              </article>
            ))}
          </div>
          <p className="mt-10 text-[13px] text-[#0d1117]/60 italic">{t("projects.note")}</p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 md:py-28 bg-[#f0e7d3]">
        <div className="zv-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-6">
              <div className="zv-mono-tag text-[#c8622a] mb-4">{t("stats.eyebrow")}</div>
              <h2 className="zv-serif text-4xl md:text-6xl leading-tight">{t("stats.title")}</h2>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={i} className="border-t border-[#0d1117]/20 pt-6">
                <div className="zv-serif text-5xl md:text-6xl text-[#0d1117]">{s.n}</div>
                {s.u && <div className="text-[13px] mt-1 text-[#0d1117]/60 uppercase tracking-wider">{s.u}</div>}
                <div className="mt-3 text-[14px] text-[#2a303b] leading-relaxed">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section id="values" className="py-24 md:py-32">
        <div className="zv-container">
          <div className="max-w-2xl">
            <div className="zv-mono-tag text-[#c8622a] mb-4">{t("values.eyebrow")}</div>
            <h2 className="zv-serif text-4xl md:text-6xl leading-tight">{t("values.title")}</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
            {values.map((v, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <div key={v.t} className="p-8 rounded-3xl border border-[#0d1117]/15 bg-white/50 hover:bg-white transition">
                  <div className="w-12 h-12 rounded-2xl bg-[#0d1117] text-[#c8622a] flex items-center justify-center mb-6">
                    <Icon size={22} />
                  </div>
                  <h3 className="zv-serif text-2xl mb-2">{v.t}</h3>
                  <p className="text-[15px] text-[#2a303b] leading-relaxed">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-[#0d1117] text-[#f6f0e2] rounded-t-[40px]">
        <div className="zv-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="zv-mono-tag text-[#c8622a] mb-4">{t("faq.eyebrow")}</div>
              <h2 className="zv-serif text-4xl md:text-6xl leading-tight">{t("faq.title")}</h2>
            </div>
            <div className="lg:col-span-8 divide-y divide-white/10">
              {faqs.map((f, i) => (
                <details key={i} className="group py-6">
                  <summary className="list-none flex items-start justify-between gap-6 cursor-pointer">
                    <span className="zv-serif text-xl md:text-2xl">{f.q}</span>
                    <span className="shrink-0 w-8 h-8 rounded-full border border-white/20 flex items-center justify-center transition group-open:rotate-45">
                      <span className="text-[#c8622a] text-lg leading-none">+</span>
                    </span>
                  </summary>
                  <p className="mt-4 text-[15px] text-white/75 leading-relaxed max-w-2xl">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Contact */}
      <section id="contact" className="py-24 md:py-32">
        <div className="zv-container grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="zv-mono-tag text-[#c8622a] mb-4">{t("cta.eyebrow")}</div>
            <h2 className="zv-serif text-5xl md:text-7xl leading-[1]">{t("cta.title")}</h2>
            <p className="mt-6 text-[16px] text-[#2a303b] max-w-md leading-relaxed">{t("cta.lead")}</p>
            <div className="mt-10 space-y-3 text-[14px] text-[#0d1117]/70">
              <div>hola@zantravora.com</div>
              <div>Av. Córdoba 1234, Piso 8 — CABA</div>
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 bg-[#f0e7d3] rounded-[28px] p-8 md:p-10">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}
